import React, { useState } from 'react';
import { Info, X, ExternalLink, FileText, TrendingUp } from 'lucide-react';
import { useTranslation } from "../translations";

/**
 * Tooltip com guia visual para critérios de avaliação
 * Baseado em parâmetros técnicos da heveicultura brasileira
 * Links para documentos oficiais públicos (INCAPER, CATI/SP)
 * 
 * DISCLAIMER: HeveaTalent não é afiliado a INCAPER ou CATI.
 * Os critérios técnicos são baseados em literatura pública da área.
 * 
 * Props:
 * - criterion: string - chave do critério (ex: 'angle_cutting')
 * - maxScore: number - pontuação máxima (default: 10)
 * - currentScore: number (opcional) - pontuação atual para mostrar indicador visual
 */
const EvaluationTooltip = ({ criterion, maxScore = 10, currentScore = null }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Função para determinar a categoria da pontuação atual
  const getScoreCategory = (score, max) => {
    if (score === null || score === undefined) return null;
    const percentage = (score / max) * 100;
    if (percentage >= 85) return 'ideal';
    if (percentage >= 55) return 'acceptable';
    return 'inadequate';
  };

  // Guias de avaliação por critério - com referências para literatura técnica brasileira
  const guides = {
    angle_cutting: {
      title: "Ângulo de Corte",
      context: "Padrão técnico brasileiro",
      ideal: {
        score: "13-15",
        description: "Corte a 33° (padrão técnico)",
        details: [
          "📐 Gabarito de 33° encaixa perfeitamente",
          "💧 Látex escorre uniforme até a bica"
        ]
      },
      acceptable: {
        score: "8-12",
        description: "30-37° - funcional com pequenos desvios",
        details: [
          "Gabarito apresenta leve folga",
          "Látex escorre com irregularidades"
        ]
      },
      inadequate: {
        score: "0-7",
        description: "<30° ou >40° - compromete produção",
        details: [
          "❌ Gabarito não encaixa",
          "⚠️ Látex coagula ou escorre errado"
        ]
      },
      practicalExample: {
        howToDo: {
          title: "🔧 Como Fazer",
          steps: [
            "1️⃣ Encoste o gabarito de 33° no ponto inicial",
            "2️⃣ DESTRO: corte da esquerda → direita",
            "3️⃣ Mantenha a faca na linha do gabarito",
            "4️⃣ Movimento contínuo até o final"
          ]
        },
        howToMeasure: {
          title: "📏 Como Medir",
          tools: ["Gabarito/bandeira de 33°"],
          steps: [
            "Encoste o gabarito no corte",
            "✅ Encaixe perfeito = ângulo correto",
            "❌ Folga ou não encaixa = ajustar"
          ]
        },
        tips: [
          "💡 33° para descendente, 45° para ascendente"
        ]
      },
      reference: "INCAPER Guia Prático Págs. 11 e 15 • CATI Manual 72 Pág. 120",
      officialDocs: [
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 4.5 - Marcação do canal de sangria (ângulo 33° inicial)",
          page: "Página 11",
          figure: "Formação do ângulo de 33° em relação ao solo",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=11",
          external: true
        },
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 8.a - Declividade do corte (manutenção do 33°)",
          page: "Página 15",
          figure: "Manutenção da declividade inicial",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=15",
          external: true
        },
        {
          source: "CATI/SP",
          title: "Manual Técnico 72 - Cultura da Seringueira",
          description: "Marcação do ângulo de corte (33° a 37° descendente, 45° ascendente)",
          page: "Página 120",
          figure: "Figuras 111-112: Demarcação do ângulo com bandeira",
          url: "https://www.cati.sp.gov.br/portal/themes/unify/arquivos/produtos-e-servicos/acervo-tecnico/Manual%20T%C3%A9cnico%2072%20-%20A%20CULTURA%20DA%20SERINGUEIRA%20PARA%20O%20ESTADO%20DE%20S%C3%83O%20PAULO.pdf#page=120",
          external: true
        }
      ]
    },
    cutting_depth: {
      title: "Profundidade de Corte",
      context: "Padrão técnico brasileiro",
      ideal: {
        score: "13-15",
        description: "1,5mm do câmbio - cor BRANCA",
        details: [
          "🎨 Corte branco leitoso = profundidade ideal",
          "💧 Látex escorre abundante"
        ]
      },
      acceptable: {
        score: "8-12",
        description: "1,5-2mm - cor marfim claro",
        details: [
          "Levemente raso, mas funcional",
          "Látex escorre adequadamente"
        ]
      },
      inadequate: {
        score: "0-7",
        description: "Muito raso (amarelo) ou profundo (rosa)",
        details: [
          "🟡 Amarelo = raso demais",
          "🔴 Rosa/vermelho = PERIGO! Atingiu câmbio"
        ]
      },
      practicalExample: {
        howToDo: {
          title: "🔧 Como Fazer",
          steps: [
            "1️⃣ Ajuste a profundidade da faca antes de cortar",
            "2️⃣ Observe a COR durante o corte",
            "3️⃣ BRANCO = ideal | ROSA = pare imediatamente"
          ]
        },
        howToMeasure: {
          title: "📏 Como Medir",
          tools: ["Observação da cor da casca"],
          steps: [
            "BRANCO LEITOSO → Profundidade ideal ✓",
            "AMARELADO → Muito raso, aprofundar",
            "ROSADO → Muito profundo, PARAR!"
          ]
        },
        tips: [
          "💡 A COR é o melhor indicador - prefira errar para raso"
        ]
      },
      reference: "INCAPER Guia Prático Pág. 16 • CATI Manual 72 Pág. 123",
      officialDocs: [
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 8.c - Profundidade de sangria (1,5mm do câmbio)",
          page: "Página 16",
          figure: "Anatomia da casca e profundidade ideal",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=15",
          external: true
        },
        {
          source: "CATI/SP",
          title: "Manual Técnico 72 - Cultura da Seringueira",
          description: "Sangria: corte sem ferimentos na região cambial (1,2-1,5mm)",
          page: "Página 123",
          figure: "Figura 116: Técnica de sangria",
          url: "https://www.cati.sp.gov.br/portal/themes/unify/arquivos/produtos-e-servicos/acervo-tecnico/Manual%20T%C3%A9cnico%2072%20-%20A%20CULTURA%20DA%20SERINGUEIRA%20PARA%20O%20ESTADO%20DE%20S%C3%83O%20PAULO.pdf#page=123",
          external: true
        }
      ]
    },
    bark_consumption: {
      title: "Consumo de Casca",
      context: "Padrão técnico brasileiro",
      ideal: {
        score: "6.5-7.5",
        description: "1,2mm por sangria",
        details: [
          "📏 Tira fina (espessura de 2 folhas de papel)",
          "🌳 Painel dura 20-25 anos"
        ]
      },
      acceptable: {
        score: "4-6",
        description: "1,0-1,5mm por sangria",
        details: [
          "Levemente grosso ou fino",
          "Painel dura 15-20 anos"
        ]
      },
      inadequate: {
        score: "0-3.5",
        description: ">2mm por sangria",
        details: [
          "❌ Tira grossa = painel esgota rápido",
          "⏰ Vida útil: apenas 8-10 anos"
        ]
      },
      practicalExample: {
        howToDo: {
          title: "🔧 Como Fazer",
          steps: [
            "1️⃣ Ajuste a faca para remover apenas 1,2mm",
            "2️⃣ Corte contínuo, sem pressão excessiva",
            "3️⃣ Tira ideal = espessura de 2 folhas de papel"
          ]
        },
        howToMeasure: {
          title: "📏 Como Medir",
          tools: ["Régua milimetrada"],
          steps: [
            "Meça a largura da tira removida",
            "Consumo anual ideal: ~15cm",
            "Cálculo: distância ÷ nº sangrias = consumo/sangria"
          ],
          example: "Ex: 12mm em 10 sangrias = 1,2mm/sangria ✓"
        },
        tips: [
          "💡 Faca afiada = corte mais fino e uniforme"
        ]
      },
      reference: "INCAPER Guia Prático Pág. 16 • CATI Manual 72 Pág. 123",
      officialDocs: [
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 8.d - Consumo de casca (~1,2mm por sangria)",
          page: "Página 16",
          figure: "Parâmetros técnicos de consumo",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=16",
          external: true
        },
        {
          source: "CATI/SP",
          title: "Manual Técnico 72 - Cultura da Seringueira",
          description: "Consumo de casca: 1,2-1,5mm/sangria, determinante para vida útil",
          page: "Página 123",
          figure: "Controle com marcador de consumo de casca",
          url: "https://www.cati.sp.gov.br/portal/themes/unify/arquivos/produtos-e-servicos/acervo-tecnico/Manual%20T%C3%A9cnico%2072%20-%20A%20CULTURA%20DA%20SERINGUEIRA%20PARA%20O%20ESTADO%20DE%20S%C3%83O%20PAULO.pdf#page=123",
          external: true
        }
      ]
    },
    generatrix_marking: {
      title: "Geratriz e Demarcação",
      context: "Padrão técnico brasileiro",
      ideal: {
        score: "6.5-7.5",
        description: "Linha vertical reta e bem marcada",
        details: [
          "📏 Riscador produziu linha perfeitamente vertical",
          "✅ Corte segue a linha sem ajustes"
        ]
      },
      acceptable: {
        score: "4-6",
        description: "Pequena ondulação, mas funcional",
        details: [
          "Linha com leve desvio",
          "Marcação parcialmente visível"
        ]
      },
      inadequate: {
        score: "0-3.5",
        description: "Linha torta - compromete o painel",
        details: [
          "❌ Corte em zigue-zague",
          "💸 Desperdício de casca útil"
        ]
      },
      practicalExample: {
        howToDo: {
          title: "🔧 Como Fazer",
          steps: [
            "1️⃣ Apoie o riscador no chão verticalmente",
            "2️⃣ Passe de baixo para cima em movimento único",
            "3️⃣ Marque com tinta/giz para visualização"
          ]
        },
        howToMeasure: {
          title: "📏 Como Medir",
          tools: ["Riscador", "Fio de prumo (opcional)"],
          steps: [
            "Olhe de longe - deve parecer perfeitamente vertical",
            "✅ Corte segue a linha sem correções = ideal"
          ]
        },
        tips: [
          "💡 Geratriz torta compromete TODAS as sangrias futuras"
        ]
      },
      reference: "INCAPER Guia Prático Págs. 10-11 • CATI Manual 72 Pág. 119",
      officialDocs: [
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 4.5 - Marcação e abertura do canal de sangria",
          page: "Páginas 10-11",
          figure: "Traçado da geratriz com riscador",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=10",
          external: true
        },
        {
          source: "CATI/SP",
          title: "Manual Técnico 72 - Cultura da Seringueira",
          description: "Demarcação das linhas geratrizes: duas linhas verticais opostas",
          page: "Página 119",
          figure: "Figuras 109-110: Medição e demarcação das geratrizes",
          url: "https://www.cati.sp.gov.br/portal/themes/unify/arquivos/produtos-e-servicos/acervo-tecnico/Manual%20T%C3%A9cnico%2072%20-%20A%20CULTURA%20DA%20SERINGUEIRA%20PARA%20O%20ESTADO%20DE%20S%C3%83O%20PAULO.pdf#page=119",
          external: true
        }
      ]
    },
    gutter_cleaning: {
      title: "Limpeza de Bica",
      context: "Padrão técnico brasileiro",
      ideal: {
        score: "2",
        description: "Bica limpa, látex escorre livre",
        details: [
          "💧 Sem resíduos ou obstruções",
          "🧹 Limpeza diária antes da sangria"
        ]
      },
      acceptable: {
        score: "1",
        description: "Pequenos resíduos, funcional",
        details: [
          "Látex escorre com leve obstrução",
          "Precisa limpeza regular"
        ]
      },
      inadequate: {
        score: "0",
        description: "Bica entupida - perda de produção",
        details: [
          "❌ Látex acumula e coagula",
          "🦠 Risco de doenças no painel"
        ]
      },
      practicalExample: {
        howToDo: {
          title: "🔧 Como Fazer",
          steps: [
            "1️⃣ Inspecione antes de sangrar",
            "2️⃣ Remova cernambi seco com espátula",
            "3️⃣ Verifique encaixe e inclinação"
          ]
        },
        howToMeasure: {
          title: "📏 Como Medir",
          tools: ["Inspeção visual"],
          steps: [
            "Látex deve escorrer em <3 segundos",
            "✅ Escorre direto na tigela = OK"
          ]
        },
        tips: [
          "💡 Bica mal encaixada = perda de 20% da produção"
        ]
      },
      reference: "INCAPER Guia Prático Págs. 12-15 • CATI Manual 72 Pág. 121",
      officialDocs: [
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 8.e - Limpeza da bica e retirada de cernambi",
          page: "Página 15",
          figure: "Procedimento de limpeza",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=15",
          external: true
        },
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 4.6 - Equipagem das plantas (bica, suporte, tigela)",
          page: "Páginas 12-13",
          figure: "Figuras 6-7: Posição correta da bica",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=12",
          external: true
        },
        {
          source: "CATI/SP",
          title: "Manual Técnico 72 - Cultura da Seringueira",
          description: "Equipagem das árvores: bica, suporte e tigela",
          page: "Página 121",
          figure: "Figura 115: Distância para colocação da bica",
          url: "https://www.cati.sp.gov.br/portal/themes/unify/arquivos/produtos-e-servicos/acervo-tecnico/Manual%20T%C3%A9cnico%2072%20-%20A%20CULTURA%20DA%20SERINGUEIRA%20PARA%20O%20ESTADO%20DE%20S%C3%83O%20PAULO.pdf#page=121",
          external: true
        }
      ]
    },
    tapping_recovery: {
      title: "Recuperação de Sangria",
      context: "Padrão técnico brasileiro - Regularidade",
      ideal: {
        score: "5-6",
        description: "Sem dias perdidos ou reposições feitas",
        details: [
          "✅ Mês completo sem falhas, OU",
          "🔄 Teve perdas mas repôs"
        ]
      },
      acceptable: {
        score: "3-4",
        description: "Poucas reposições",
        details: [
          "Teve perdas e repôs parcialmente"
        ]
      },
      inadequate: {
        score: "0-2",
        description: "Sem reposições",
        details: [
          "❌ Teve perdas e não repôs"
        ]
      },
      practicalExample: {
        howToDo: {
          title: "🔧 Como Avaliar",
          steps: [
            "1️⃣ Houve dias perdidos no mês?",
            "2️⃣ Se SIM → Fez reposições?",
            "3️⃣ Se NÃO houve perdas → Nota máxima"
          ]
        },
        tips: [
          "💡 Recuperar dias perdidos pode aumentar a produção em até 20-30%"
        ]
      },
      reference: "IEA-SP Documento Técnico 1992 - Gestão de Seringais",
      officialDocs: [
        {
          source: "IEA-SP",
          title: "Documento Técnico - Gestão de Seringais",
          description: "Reposição de dias perdidos e impacto na produtividade (20-30%)",
          page: "Técnico 3-0792",
          figure: "Modelos de produção e reposição",
          url: "https://iea.agricultura.sp.gov.br/ftpiea/ie/1992/tec3-0792.pdf",
          external: true
        }
      ]
    },
    injury_prevention: {
      title: "Prevenção de Ferimentos",
      context: "Padrão técnico brasileiro",
      ideal: {
        score: "6-7",
        description: "Painel sem ferimentos no câmbio",
        details: [
          "✅ Nenhum ponto rosa/vermelho",
          "💚 Painel saudável e produtivo"
        ]
      },
      acceptable: {
        score: "4-5",
        description: "1-2 ferimentos leves, tratados",
        details: [
          "Pontos pequenos já cicatrizados",
          "Produção não afetada"
        ]
      },
      inadequate: {
        score: "0-3",
        description: "Múltiplos ferimentos ou painel seco",
        details: [
          "🔴 Cortes atingiram câmbio",
          "⚠️ Sinais de TPD (painel seco)"
        ]
      },
      practicalExample: {
        howToDo: {
          title: "🔧 Como Prevenir",
          steps: [
            "1️⃣ Ajuste faca: máx 1,5mm do câmbio",
            "2️⃣ Se aparecer ROSA: PARE imediatamente",
            "3️⃣ Trate com pasta cicatrizante em 24h"
          ]
        },
        howToMeasure: {
          title: "📏 Como Avaliar",
          tools: ["Inspeção visual"],
          steps: [
            "🔴 Rosa/vermelho = ferimento grave",
            "🟡 Arranhão superficial = aceitável",
            "✅ Branco uniforme = ideal"
          ]
        },
        tips: [
          "💡 Um ferimento grave pode causar TPD irreversível"
        ]
      },
      reference: "INCAPER Guia Prático Pág. 20 • CATI Manual 72 Pág. 123",
      officialDocs: [
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 14 - Painel seco (TPD) e prevenção",
          page: "Página 20",
          figure: "Figuras 13-14: Sintomas do painel seco",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=20",
          external: true
        },
        {
          source: "CATI/SP",
          title: "Manual Técnico 72 - Cultura da Seringueira",
          description: "Corte sem ferimentos na região cambial (responsável pela regeneração)",
          page: "Página 123",
          figure: "Técnica de sangria sem danos ao câmbio",
          url: "https://www.cati.sp.gov.br/portal/themes/unify/arquivos/produtos-e-servicos/acervo-tecnico/Manual%20T%C3%A9cnico%2072%20-%20A%20CULTURA%20DA%20SERINGUEIRA%20PARA%20O%20ESTADO%20DE%20S%C3%83O%20PAULO.pdf#page=123",
          external: true
        }
      ]
    },
    ethrel_application: {
      title: "Aplicação de Ethrel",
      context: "Padrão técnico brasileiro",
      ideal: {
        score: "6-7",
        description: "Concentração 2,5-3,3%, intervalo 30-45 dias",
        details: [
          "💉 Dose: 2mL por planta",
          "✅ Sem sinais de estresse"
        ]
      },
      acceptable: {
        score: "4-5",
        description: "Pequenos desvios, sem danos",
        details: [
          "Intervalo 25-50 dias",
          "Sem secamento do painel"
        ]
      },
      inadequate: {
        score: "0-3",
        description: "Dose errada ou excesso",
        details: [
          "❌ Concentração >3,3%",
          "🚨 Sinais de TPD (painel seco)"
        ]
      },
      practicalExample: {
        howToDo: {
          title: "🔧 Como Fazer",
          steps: [
            "1️⃣ Diluição: 1 parte Ethrel 10% + 3 partes água = 2,5%",
            "2️⃣ Aplique 2mL na casca acima do corte",
            "3️⃣ Intervalo mínimo: 30 dias"
          ]
        },
        howToMeasure: {
          title: "📏 Como Medir",
          tools: ["Copo medidor", "Seringa 10mL"],
          steps: [
            "100mL Ethrel 10% + 300mL água = 400mL a 2,5%"
          ],
          example: "1L de solução rende ~500 árvores"
        },
        tips: [
          "💡 Nunca aplique em árvore com painel seco"
        ]
      },
      reference: "INCAPER Guia Prático Págs. 18-19 • CATI Manual 72 Pág. 124",
      ethrelTable: {
        title: "Dosagem por Sistema",
        headers: ["Sistema", "Concentração", "Intervalo"],
        rows: [
          ["d/3 a d/7", "2,5-3,3%", "30-45 dias"]
        ],
        notes: [
          "⚠️ >3,3% aumenta risco de TPD"
        ]
      },
      officialDocs: [
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 13 - Estimulante (Ethrel) e EPIs obrigatórios",
          page: "Páginas 18-19",
          figure: "Figura 12: Aplicação de Ethrel com EPIs",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=18",
          external: true
        },
        {
          source: "CATI/SP",
          title: "Manual Técnico 72 - Cultura da Seringueira",
          description: "Estimulação química: ET 2,5% e frequências de aplicação",
          page: "Página 124",
          figure: "Modo de aplicação e balanceamento",
          url: "https://www.cati.sp.gov.br/portal/themes/unify/arquivos/produtos-e-servicos/acervo-tecnico/Manual%20T%C3%A9cnico%2072%20-%20A%20CULTURA%20DA%20SERINGUEIRA%20PARA%20O%20ESTADO%20DE%20S%C3%83O%20PAULO.pdf#page=124",
          external: true
        }
      ]
    },
    fungicide_application: {
      title: "Aplicação de Fungicida",
      context: "Padrão técnico brasileiro",
      ideal: {
        score: "6-7",
        description: "Aplicação preventiva semanal (período chuvoso)",
        details: [
          "🧴 Produto correto (Triadimenol)",
          "✅ Painel sem fungos"
        ]
      },
      acceptable: {
        score: "4-5",
        description: "Aplicação regular, pequenas falhas",
        details: [
          "Intervalo levemente irregular",
          "Sem infecção ativa"
        ]
      },
      inadequate: {
        score: "0-3",
        description: "Sem controle ou infecção ativa",
        details: [
          "❌ Fungo visível no painel",
          "⚠️ Aplicação ausente"
        ]
      },
      practicalExample: {
        howToDo: {
          title: "🔧 Como Fazer",
          steps: [
            "1️⃣ Prepare solução conforme bula (ex: 3g/L)",
            "2️⃣ Aplique após a sangria com pincel",
            "3️⃣ Semanalmente no período chuvoso"
          ]
        },
        howToMeasure: {
          title: "📏 Como Avaliar",
          tools: ["Inspeção visual"],
          steps: [
            "Painel sem manchas = saudável ✓"
          ]
        },
        tips: [
          "💡 Período chuvoso = maior risco de fungos"
        ]
      },
      reference: "INCAPER Guia Prático Págs. 17-18 • CATI Manual 72 Págs. 75-83",
      officialDocs: [
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 11 - Preparo do fungicida com EPIs",
          page: "Páginas 17-18",
          figure: "Figura 11: Preparo correto do fungicida",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=17",
          external: true
        },
        {
          source: "CATI/SP",
          title: "Manual Técnico 72 - Cultura da Seringueira",
          description: "Doenças do painel: Antracnose, Mofo-cinzento, Cancro-estriado e controle químico",
          page: "Páginas 75-83",
          figure: "Figuras 68-71: Sintomas e tratamento das doenças",
          url: "https://www.cati.sp.gov.br/portal/themes/unify/arquivos/produtos-e-servicos/acervo-tecnico/Manual%20T%C3%A9cnico%2072%20-%20A%20CULTURA%20DA%20SERINGUEIRA%20PARA%20O%20ESTADO%20DE%20S%C3%83O%20PAULO.pdf#page=75",
          external: true
        }
      ]
    },
    coagulation: {
      title: "Controle de Coagulação",
      context: "Padrão técnico brasileiro - Qualidade do látex",
      ideal: {
        score: "5-6",
        description: "Látex líquido até coleta final",
        details: [
          "💧 Látex fresco, branco e fluido",
          "🧪 Uso de anticoagulante (amônia) quando necessário",
          "⭐ Qualidade premium para processamento",
          "💰 Melhor preço de venda"
        ]
      },
      acceptable: {
        score: "3-4",
        description: "Pequena coagulação, mas processável",
        details: [
          "Látex com pequenos grumos",
          "Coleta dentro do tempo adequado",
          "Ainda utilizável para cernambi"
        ]
      },
      inadequate: {
        score: "0-2",
        description: "Látex coagulado precocemente",
        details: [
          "❌ Látex virou 'cernambi' na tigela",
          "📉 Baixa qualidade, preço menor",
          "⏰ Coleta atrasada ou contaminação"
        ]
      },
      reference: "INCAPER Guia Prático Págs. 16-17 • CATI Manual 72 Pág. 125",
      officialDocs: [
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 9: Coleta da produção - látex e cernambi",
          page: "Páginas 16-17",
          figure: "Figura 9 - Coleta de cernambi na tigela",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=16",
          external: true
        },
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 10: Armazenamento - uso de amônia para conservação",
          page: "Página 17",
          figure: "Texto técnico - Conservação do látex",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=17",
          external: true
        },
        {
          source: "CATI/SP",
          title: "Manual Técnico 72 - Cultura da Seringueira",
          description: "Coleta e armazenamento do látex: tigela, amônia 12%, coagulação",
          page: "Página 125",
          figure: "Figura 117: Técnicas de coleta",
          url: "https://www.cati.sp.gov.br/portal/themes/unify/arquivos/produtos-e-servicos/acervo-tecnico/Manual%20T%C3%A9cnico%2072%20-%20A%20CULTURA%20DA%20SERINGUEIRA%20PARA%20O%20ESTADO%20DE%20S%C3%83O%20PAULO.pdf#page=125",
          external: true
        }
      ]
    },
    relative_productivity: {
      title: "Produtividade Relativa",
      context: "Padrão técnico brasileiro - Avaliação contextual",
      ideal: {
        score: "9-10",
        description: "Acima da média para o contexto",
        details: [
          "📈 Produção superior à média do seringal",
          "🌳 Considera: clone, idade, clima, sistema",
          "✅ Técnica apurada e consistente",
          "💪 Trabalhador experiente e dedicado"
        ]
      },
      acceptable: {
        score: "7-8",
        description: "Dentro da média esperada",
        details: [
          "Produção compatível com o contexto",
          "Performance adequada ao seringal",
          "Sem problemas técnicos evidentes"
        ]
      },
      inadequate: {
        score: "0-6",
        description: "Abaixo da média contextual",
        details: [
          "📉 Produção inferior ao esperado",
          "🔍 Pode indicar problemas técnicos",
          "📚 Requer investigação e treinamento"
        ]
      },
      reference: "Avaliação contextual baseada em dados do seringal",
      officialDocs: [
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Documento completo sobre técnicas de sangria e produtividade",
          page: "Documento completo",
          figure: "Todas as técnicas para máxima produtividade",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=9",
          external: true
        }
      ],
      private: true
    },
    // ═══════════════════════════════════════════════════════════════
    // GRUPO B1: PREPARAÇÃO DE SAFRA (20 pontos) - NOVOS CRITÉRIOS v2
    // ═══════════════════════════════════════════════════════════════
    aptitude_evaluation: {
      title: "Avaliação de Aptidão",
      context: "Padrão técnico brasileiro",
      ideal: {
        score: "4-5",
        description: "Seleção correta de árvores",
        details: [
          "📏 Perímetro ≥ 50cm (INCAPER) - IDEAL",
          "🌳 Espessura de casca ≥ 6mm",
          "✅ Mínimo 40% das árvores aptas para iniciar"
        ]
      },
      acceptable: {
        score: "3",
        description: "Dentro do mínimo técnico",
        details: [
          "📏 Perímetro ≥ 45cm (CATI) - MÍNIMO",
          "⚠️ Árvores no limite, monitorar desenvolvimento"
        ]
      },
      inadequate: {
        score: "0-2",
        description: "Árvores imaturas sangradas",
        details: [
          "❌ Perímetro < 45cm ou casca < 6mm",
          "⚠️ Risco de prejudicar desenvolvimento da árvore"
        ]
      },
      practicalExample: {
        howToDo: {
          title: "🔧 Como Fazer",
          steps: [
            "1️⃣ Meça perímetro a 1,20-1,30m do solo",
            "2️⃣ IDEAL: ≥50cm | MÍNIMO: ≥45cm",
            "3️⃣ Meça espessura da casca (≥6mm)",
            "4️⃣ APTA: marque 'X' | NÃO APTA: marque 'O'"
          ]
        },
        howToMeasure: {
          title: "📏 Padrões de Referência",
          tools: ["Fita métrica", "Medidor de casca"],
          steps: [
            "INCAPER: ≥50cm a 1,30m + casca ≥6mm (IDEAL)",
            "CATI/SP: ≥45cm a 1,20m + casca ≥6mm (MÍNIMO)"
          ]
        },
        tips: [
          "💡 Perímetro 45cm funciona, mas acima de 50cm é mais seguro e produtivo"
        ]
      },
      reference: "INCAPER Guia Prático Págs. 8-11 • CATI Manual 72 Págs. 117-119",
      officialDocs: [
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 4 - Preparo da planta para sangria (CAP e casca)",
          page: "Páginas 8-11",
          figure: "Critérios de aptidão para sangria",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=8",
          external: true
        },
        {
          source: "CATI/SP",
          title: "Manual Técnico 72 - Cultura da Seringueira",
          description: "Parâmetros técnicos: perímetro ≥45cm a 1,20m, casca ≥6mm, 40% árvores aptas",
          page: "Páginas 117-119",
          figure: "Explotação do seringal - critérios de aptidão",
          url: "https://www.cati.sp.gov.br/portal/themes/unify/arquivos/produtos-e-servicos/acervo-tecnico/Manual%20T%C3%A9cnico%2072%20-%20A%20CULTURA%20DA%20SERINGUEIRA%20PARA%20O%20ESTADO%20DE%20S%C3%83O%20PAULO.pdf#page=117",
          external: true
        }
      ]
    },
    flagging: {
      title: "Bandeiramento",
      context: "Padrão técnico brasileiro",
      ideal: {
        score: "4-5",
        description: "Gabarito 33° encaixa perfeitamente",
        details: [
          "📐 Ângulo correto verificado com gabarito/bandeira",
          "📏 Corte segue a linha marcada com precisão"
        ]
      },
      acceptable: {
        score: "3",
        description: "Pequenos desvios no gabarito",
        details: [
          "Gabarito com leve folga",
          "Desvio até 5° aceitável"
        ]
      },
      inadequate: {
        score: "0-2",
        description: "Sem uso de gabarito",
        details: [
          "❌ Ângulos irregulares",
          "📉 Corte fora do padrão 33°"
        ]
      },
      practicalExample: {
        howToDo: {
          title: "🔧 Como Fazer",
          steps: [
            "1️⃣ Use gabarito/bandeira de 33° (descendente) ou 45° (ascendente)",
            "2️⃣ Encoste no ponto inicial do corte",
            "3️⃣ Marque a linha com giz ou traçador"
          ]
        },
        howToMeasure: {
          title: "📏 Como Medir",
          tools: ["Gabarito 33° ou 45°", "Traçador"],
          steps: [
            "Encoste o gabarito no corte já feito",
            "✅ Encaixe perfeito = ângulo correto",
            "❌ Folga = corte fora do padrão"
          ]
        },
        tips: [
          "💡 Ângulo errado compromete o escorrimento do látex"
        ]
      },
      reference: "INCAPER Guia Prático Págs. 11 e 15 • CATI Manual 72 Pág. 120",
      officialDocs: [
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 4.5 - Marcação e abertura do canal de sangria (ângulo 33°)",
          page: "Página 11",
          figure: "Formação do ângulo de 33° em relação ao solo",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=11",
          external: true
        },
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 8.a - Declividade do corte (manutenção do ângulo 33°)",
          page: "Página 15",
          figure: "Manutenção da declividade inicial",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=15",
          external: true
        },
        {
          source: "CATI/SP",
          title: "Manual Técnico 72 - Cultura da Seringueira",
          description: "Uso de bandeira/gabarito para demarcação do ângulo (33° a 37°)",
          page: "Página 120",
          figure: "Figuras 111-112: Demarcação do ângulo com bandeira",
          url: "https://www.cati.sp.gov.br/portal/themes/unify/arquivos/produtos-e-servicos/acervo-tecnico/Manual%20T%C3%A9cnico%2072%20-%20A%20CULTURA%20DA%20SERINGUEIRA%20PARA%20O%20ESTADO%20DE%20S%C3%83O%20PAULO.pdf#page=120",
          external: true
        }
      ]
    },
    equipment_installation: {
      title: "Instalação de Equipamentos",
      context: "Padrão técnico brasileiro",
      ideal: {
        score: "4-5",
        description: "Bica, caneca e arame corretos",
        details: [
          "🪣 Bica no ponto mais baixo do corte",
          "✅ 100% do látex cai na caneca"
        ]
      },
      acceptable: {
        score: "3",
        description: "Pequenos ajustes necessários",
        details: [
          "Equipamentos funcionando",
          "Mínimo desperdício"
        ]
      },
      inadequate: {
        score: "0-2",
        description: "Instalação incorreta",
        details: [
          "❌ Bica mal posicionada",
          "💧 Perda significativa de látex"
        ]
      },
      practicalExample: {
        howToDo: {
          title: "🔧 Como Fazer",
          steps: [
            "1️⃣ Bica no ponto mais baixo, inclinada",
            "2️⃣ Caneca diretamente abaixo, nivelada",
            "3️⃣ Arame tensionado (caneca não balança)"
          ]
        },
        howToMeasure: {
          title: "📏 Como Medir",
          tools: ["Teste de fluxo"],
          steps: [
            "Pingue água na bica",
            "✅ Cai dentro da caneca = OK"
          ]
        },
        tips: [
          "💡 Equipamento bem instalado = ZERO perda"
        ]
      },
      reference: "INCAPER Guia Prático Págs. 12-13 • CATI Manual 72 Pág. 122",
      officialDocs: [
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 4.6 - Equipagem das plantas (bica, suporte, tigela)",
          page: "Páginas 12-13",
          figure: "Figuras 6-7: Instalação correta",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=12",
          external: true
        },
        {
          source: "CATI/SP",
          title: "Manual Técnico 72 - Cultura da Seringueira",
          description: "Equipagem das árvores: distância para bica, árvore equipada",
          page: "Página 122",
          figure: "Figura 115: Equipagem das árvores (A, B, C)",
          url: "https://www.cati.sp.gov.br/portal/themes/unify/arquivos/produtos-e-servicos/acervo-tecnico/Manual%20T%C3%A9cnico%2072%20-%20A%20CULTURA%20DA%20SERINGUEIRA%20PARA%20O%20ESTADO%20DE%20S%C3%83O%20PAULO.pdf#page=122",
          external: true
        }
      ]
    },
    panel_opening: {
      title: "Abertura de Painel",
      context: "Padrão técnico brasileiro",
      ideal: {
        score: "4-5",
        description: "Abertura perfeita",
        details: [
          "📏 Geratrizes verticais e retas",
          "✅ Sulco uniforme, sem ferimentos"
        ]
      },
      acceptable: {
        score: "3",
        description: "Abertura funcional",
        details: [
          "Pequenos desvios",
          "Ajustes menores necessários"
        ]
      },
      inadequate: {
        score: "0-2",
        description: "Abertura incorreta",
        details: [
          "❌ Geratrizes tortas",
          "📉 Ferimentos no lenho"
        ]
      },
      practicalExample: {
        howToDo: {
          title: "🔧 Como Fazer",
          steps: [
            "1️⃣ Marque geratrizes com riscador",
            "2️⃣ Use bandeira de 45° para abertura",
            "3️⃣ Rebaixe e aprofunde gradualmente"
          ]
        },
        howToMeasure: {
          title: "📏 Como Medir",
          tools: ["Riscador", "Bandeira 45°"],
          steps: [
            "Geratriz vertical + sulco uniforme = OK ✓"
          ]
        },
        tips: [
          "💡 Abertura correta = painel de 25+ anos de exploração"
        ]
      },
      reference: "INCAPER Guia Prático Págs. 10-13 • CATI Manual 72 Pág. 122",
      officialDocs: [
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 4 - Abertura de painel e preparação",
          page: "Páginas 10-13",
          figure: "Abertura e equipagem para sangria",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=10",
          external: true
        },
        {
          source: "CATI/SP",
          title: "Manual Técnico 72 - Cultura da Seringueira",
          description: "Abertura do painel: desbaste, canaleta, sulco uniforme",
          page: "Página 122",
          figure: "Figura 114: Painel aberto",
          url: "https://www.cati.sp.gov.br/portal/themes/unify/arquivos/produtos-e-servicos/acervo-tecnico/Manual%20T%C3%A9cnico%2072%20-%20A%20CULTURA%20DA%20SERINGUEIRA%20PARA%20O%20ESTADO%20DE%20S%C3%83O%20PAULO.pdf#page=122",
          external: true
        }
      ]
    },
    // Alias para compatibilidade com v2
    coagulation_control: {
      title: "Controle de Coagulação",
      context: "Padrão técnico brasileiro",
      ideal: {
        score: "5-6",
        description: "Látex líquido até coleta",
        details: [
          "💧 Branco e fluido",
          "💰 Melhor preço de venda"
        ]
      },
      acceptable: {
        score: "3-4",
        description: "Pequena coagulação",
        details: [
          "Látex com pequenos grumos",
          "Ainda utilizável"
        ]
      },
      inadequate: {
        score: "0-2",
        description: "Coagulado (cernambi)",
        details: [
          "❌ Virou cernambi na tigela",
          "📉 Preço menor"
        ]
      },
      practicalExample: {
        howToDo: {
          title: "🔧 Como Fazer",
          steps: [
            "1️⃣ Sangre cedo (5h-6h da manhã)",
            "2️⃣ Colete em até 4 horas",
            "3️⃣ Tigela sempre limpa"
          ]
        },
        howToMeasure: {
          title: "📏 Como Avaliar",
          tools: ["Inspeção visual"],
          steps: [
            "Branco e fluido = qualidade ✓",
            "Grumos = coagulou cedo ✗"
          ]
        },
        tips: [
          "💡 Tigela suja = principal causa de coagulação"
        ]
      },
      reference: "INCAPER Guia Prático Págs. 16-17 • CATI Manual 72 Pág. 125",
      officialDocs: [
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 9 - Coleta da produção (látex e cernambi)",
          page: "Páginas 16-17",
          figure: "Figuras 9-10: Coleta e transporte do látex",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=16",
          external: true
        },
        {
          source: "INCAPER",
          title: "Guia Prático do Seringueiro",
          description: "Seção 10 - Armazenamento (uso de amônia)",
          page: "Página 17",
          figure: "Conservação do látex",
          url: "https://biblioteca.incaper.es.gov.br/digital/bitstream/item/487/1/Sangria-da-Seringueira-2013-AInfo.pdf#page=17",
          external: true
        },
        {
          source: "CATI/SP",
          title: "Manual Técnico 72 - Cultura da Seringueira",
          description: "Coleta e armazenamento: amônia 12%, coagulação com ácido acético",
          page: "Página 125",
          figure: "Técnicas de coleta e conservação do látex",
          url: "https://www.cati.sp.gov.br/portal/themes/unify/arquivos/produtos-e-servicos/acervo-tecnico/Manual%20T%C3%A9cnico%2072%20-%20A%20CULTURA%20DA%20SERINGUEIRA%20PARA%20O%20ESTADO%20DE%20S%C3%83O%20PAULO.pdf#page=125",
          external: true
        }
      ]
    }
  };

  const guide = guides[criterion];
  if (!guide) return null;

  return (
    <div className="inline-block relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="ml-2 text-blue-500 hover:text-blue-700 transition-colors"
        title="Ver guia de avaliação técnica"
      >
        <Info size={18} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => setIsOpen(false)}
          />
          {/* Modal Container */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            {/* Modal Content */}
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto pointer-events-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-800 text-white p-4 rounded-t-lg flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold">📖 {guide.title}</h3>
                <p className="text-xs text-green-100 mt-1">{guide.context || 'Padrão técnico brasileiro'}</p>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Context Info */}
              {guide.context && (
                <div className="bg-green-50 border-l-4 border-green-500 pl-4 py-2 rounded">
                  <p className="text-sm text-green-900">
                    <span className="font-semibold">📌 Contexto:</span> {guide.context}
                  </p>
                </div>
              )}

              {/* Current Score Indicator - Only shown when currentScore is provided */}
              {currentScore !== null && (
                <div className={`border-2 rounded-lg p-4 ${
                  getScoreCategory(currentScore, maxScore) === 'ideal' 
                    ? 'bg-gradient-to-r from-green-100 to-emerald-100 border-green-400' 
                    : getScoreCategory(currentScore, maxScore) === 'acceptable'
                    ? 'bg-gradient-to-r from-yellow-100 to-amber-100 border-yellow-400'
                    : 'bg-gradient-to-r from-red-100 to-orange-100 border-red-400'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className={`w-5 h-5 ${
                        getScoreCategory(currentScore, maxScore) === 'ideal' ? 'text-green-600' 
                        : getScoreCategory(currentScore, maxScore) === 'acceptable' ? 'text-yellow-600'
                        : 'text-red-600'
                      }`} />
                      <span className="font-bold text-gray-800">Sua pontuação atual:</span>
                    </div>
                    <span className={`text-2xl font-bold ${
                      getScoreCategory(currentScore, maxScore) === 'ideal' ? 'text-green-700' 
                      : getScoreCategory(currentScore, maxScore) === 'acceptable' ? 'text-yellow-700'
                      : 'text-red-700'
                    }`}>
                      {currentScore}/{maxScore}
                    </span>
                  </div>
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full transition-all ${
                          getScoreCategory(currentScore, maxScore) === 'ideal' ? 'bg-green-500' 
                          : getScoreCategory(currentScore, maxScore) === 'acceptable' ? 'bg-yellow-500'
                          : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min((currentScore / maxScore) * 100, 100)}%` }}
                      />
                    </div>
                    <p className={`text-xs mt-1 font-medium ${
                      getScoreCategory(currentScore, maxScore) === 'ideal' ? 'text-green-700' 
                      : getScoreCategory(currentScore, maxScore) === 'acceptable' ? 'text-yellow-700'
                      : 'text-red-700'
                    }`}>
                      {getScoreCategory(currentScore, maxScore) === 'ideal' 
                        ? '🏆 Excelente! Você está na faixa ideal.'
                        : getScoreCategory(currentScore, maxScore) === 'acceptable'
                        ? '📈 Bom! Há espaço para melhoria.'
                        : '⚠️ Atenção! Veja as dicas abaixo para melhorar.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Ideal */}
              <div className="border-l-4 border-green-500 pl-4 py-2 bg-green-50 rounded">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">✅</span>
                  <div>
                    <span className="font-bold text-green-700">IDEAL ({guide.ideal.score} pontos)</span>
                    <p className="text-sm text-gray-700 font-semibold">{guide.ideal.description}</p>
                  </div>
                </div>
                <ul className="text-sm text-gray-700 space-y-1 ml-8">
                  {guide.ideal.details.map((detail, i) => (
                    <li key={i}>• {detail}</li>
                  ))}
                </ul>
              </div>

              {/* Acceptable */}
              <div className="border-l-4 border-yellow-500 pl-4 py-2 bg-yellow-50 rounded">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <span className="font-bold text-yellow-700">ACEITÁVEL ({guide.acceptable.score} pontos)</span>
                    <p className="text-sm text-gray-700 font-semibold">{guide.acceptable.description}</p>
                  </div>
                </div>
                <ul className="text-sm text-gray-700 space-y-1 ml-8">
                  {guide.acceptable.details.map((detail, i) => (
                    <li key={i}>• {detail}</li>
                  ))}
                </ul>
              </div>

              {/* Inadequate */}
              <div className="border-l-4 border-red-500 pl-4 py-2 bg-red-50 rounded">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">❌</span>
                  <div>
                    <span className="font-bold text-red-700">INADEQUADO ({guide.inadequate.score} pontos)</span>
                    <p className="text-sm text-gray-700 font-semibold">{guide.inadequate.description}</p>
                  </div>
                </div>
                <ul className="text-sm text-gray-700 space-y-1 ml-8">
                  {guide.inadequate.details.map((detail, i) => (
                    <li key={i}>• {detail}</li>
                  ))}
                </ul>
              </div>

              {/* Practical Example - Como Fazer e Como Medir */}
              {guide.practicalExample && (
                <div className="bg-gradient-to-r from-cyan-50 to-teal-50 border-2 border-teal-400 rounded-lg p-4 mt-4 shadow-sm">
                  <h4 className="font-bold text-teal-900 mb-3 text-lg flex items-center gap-2">
                    🎯 Exemplo Prático
                  </h4>
                  
                  {/* Como Fazer */}
                  {guide.practicalExample.howToDo && (
                    <div className="bg-white rounded-lg p-3 mb-3 border border-teal-200">
                      <h5 className="font-bold text-teal-800 mb-2">{guide.practicalExample.howToDo.title}</h5>
                      <ul className="text-sm text-gray-700 space-y-1">
                        {guide.practicalExample.howToDo.steps.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Como Medir */}
                  {guide.practicalExample.howToMeasure && (
                    <div className="bg-white rounded-lg p-3 mb-3 border border-teal-200">
                      <h5 className="font-bold text-teal-800 mb-2">{guide.practicalExample.howToMeasure.title}</h5>
                      
                      {guide.practicalExample.howToMeasure.tools && (
                        <div className="mb-2">
                          <span className="text-xs font-semibold text-gray-600">🔧 Ferramentas: </span>
                          <span className="text-xs text-gray-600">{guide.practicalExample.howToMeasure.tools.join(' • ')}</span>
                        </div>
                      )}
                      
                      <ul className="text-sm text-gray-700 space-y-1">
                        {guide.practicalExample.howToMeasure.steps.map((step, i) => (
                          <li key={i} className="whitespace-pre-line">{step}</li>
                        ))}
                      </ul>
                      
                      {guide.practicalExample.howToMeasure.example && (
                        <div className="mt-2 bg-teal-100 rounded p-2">
                          <p className="text-sm text-teal-900 font-medium">{guide.practicalExample.howToMeasure.example}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Dicas */}
                  {guide.practicalExample.tips && guide.practicalExample.tips.length > 0 && (
                    <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-300">
                      <h5 className="font-bold text-yellow-800 mb-2">💡 Dicas do Profissional</h5>
                      <ul className="text-sm text-gray-700 space-y-1">
                        {guide.practicalExample.tips.map((tip, i) => (
                          <li key={i}>{tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Ethrel Table - Tabela específica para Ethrel */}
              {guide.ethrelTable && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-300 rounded-lg p-4 mt-4 shadow-sm">
                  <h4 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                    📋 {guide.ethrelTable.title}
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-purple-100">
                          {guide.ethrelTable.headers.map((header, i) => (
                            <th key={i} className="px-3 py-2 text-left font-semibold text-purple-900 border-b border-purple-200">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {guide.ethrelTable.rows.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-purple-50'}>
                            {row.map((cell, j) => (
                              <td key={j} className="px-3 py-2 border-b border-purple-100 text-gray-700">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 space-y-1">
                    {guide.ethrelTable.notes.map((note, i) => (
                      <p key={i} className="text-xs text-purple-800">{note}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Reference */}
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-4">
                <p className="text-sm text-blue-900">
                  <span className="font-semibold">📚 Referência Oficial:</span> {guide.reference}
                </p>
              </div>

              {/* Official Documents - Links para documentos oficiais */}
              {guide.officialDocs && guide.officialDocs.length > 0 && (
                <div className="bg-gradient-to-r from-amber-50 to-green-50 border-2 border-green-400 rounded-lg p-4 mt-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="text-green-700" size={24} />
                    <div>
                      <p className="text-sm font-bold text-green-900">📄 Consultar Figuras e Diagramas Oficiais</p>
                      <p className="text-xs text-gray-600">Clique para abrir o documento de referência</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {guide.officialDocs.map((doc, index) => {
                      // Cores por fonte - apenas fontes públicas permitidas
                      const sourceColors = {
                        INCAPER: { bg: 'bg-green-600', hover: 'hover:bg-green-100', border: 'border-green-200 hover:border-green-400', icon: 'bg-green-100 group-hover:bg-green-200', iconColor: 'text-green-700' },
                        'CATI/SP': { bg: 'bg-purple-600', hover: 'hover:bg-purple-100', border: 'border-purple-200 hover:border-purple-400', icon: 'bg-purple-100 group-hover:bg-purple-200', iconColor: 'text-purple-700' },
                        'IEA-SP': { bg: 'bg-blue-600', hover: 'hover:bg-blue-100', border: 'border-blue-200 hover:border-blue-400', icon: 'bg-blue-100 group-hover:bg-blue-200', iconColor: 'text-blue-700' }
                      };
                      const colors = sourceColors[doc.source] || sourceColors.INCAPER;
                      
                      // Handler de clique - todos links são externos agora
                      const handleClick = () => {
                        window.open(doc.url, '_blank', 'noopener,noreferrer');
                      };
                      
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={handleClick}
                          className={`w-full text-left bg-white border ${colors.border} ${colors.hover} p-3 rounded-lg transition-all shadow-sm hover:shadow-md group`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`${colors.icon} p-2 rounded-lg transition-colors`}>
                              <ExternalLink className={colors.iconColor} size={20} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-bold ${colors.bg} text-white px-2 py-0.5 rounded`}>
                                  {doc.source}
                                </span>
                                <span className="text-xs text-gray-500">{doc.page}</span>
                                <span className="text-xs text-blue-600 font-medium">↗ Documento Oficial</span>
                              </div>
                              <p className="font-semibold text-gray-800 text-sm mt-1">{doc.title}</p>
                              <p className="text-xs text-gray-600 mt-1">{doc.description}</p>
                              <p className={`text-xs ${colors.iconColor} font-medium mt-1`}>
                                📷 {doc.figure}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="bg-amber-100 border border-amber-300 rounded p-2 mt-3">
                    <p className="text-xs text-amber-800 flex items-center gap-1">
                      <span>💡</span>
                      <span><strong>Dica:</strong> Links externos abrem o documento oficial na página indicada.</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Private notice */}
              {guide.private && (
                <div className="bg-purple-50 border border-purple-200 rounded p-3 mt-2">
                  <p className="text-sm text-purple-900">
                    <span className="font-semibold">🔒 Privacidade LGPD:</span> Este critério é confidencial. Apenas o próprio trabalhador, administradores e avaliadores autorizados podem visualizar a pontuação.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-4 rounded-b-lg border-t">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded transition-colors"
              >
                {t('understood')} ✓
              </button>
            </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EvaluationTooltip;
